import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ChevronRight, Plus, XCircle } from "lucide-react";

import { ordersService } from "../../../services/orders";
import { damagesService } from "../../../services/damages";
import { formatApiError } from "../../../services/api";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { Button } from "../../../components/ui/button";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { FullPageSpinner } from "../../../components/feedback/FullPageSpinner";
import { StatusTimeline } from "../components/StatusTimeline";
import { CancelOrderModal } from "../components/CancelOrderModal";
import { TransitionModal } from "../components/TransitionModal";
import { DamageFormModal } from "../../damages/components/DamageFormModal";
import { DAMAGE_STATUS_TONE, SEVERITY_TONE, validNextOptions } from "../lifecycle";
import { useAuth } from "../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../app/permissions";
import {
  formatDate,
  formatDateRange,
  formatDateTime,
} from "../../../utils/dates";

function InfoCard({ title, children, testId }) {
  return (
    <section
      className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card"
      data-testid={testId}
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        <div className="h-px w-6 bg-champagne" aria-hidden />
      </div>
      {children}
    </section>
  );
}

function AddressBlock({ address }) {
  if (!address) return null;
  return (
    <div className="text-sm leading-relaxed text-text-primary">
      <div className="font-semibold">{address.company}</div>
      <div className="text-text-primary">
        {address.street}
        <br />
        {address.postal_code} {address.city}
        <br />
        {address.country}
        {address.vat_id ? ` · ${address.vat_id}` : ""}
      </div>
      {(address.contact_email || address.contact_phone) && (
        <div className="mt-2 text-xs text-text-secondary">
          {address.contact_email}
          {address.contact_email && address.contact_phone ? " · " : ""}
          {address.contact_phone}
        </div>
      )}
    </div>
  );
}

export default function OrderDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const [damageOpen, setDamageOpen] = useState(false);
  const [damageSaving, setDamageSaving] = useState(false);

  const [transitionOpen, setTransitionOpen] = useState(false);

  const canAddDamage =
    hasPermission(user, Permission.ORDER_DAMAGES_CREATE) &&
    order?.status === "quality_testing";
  // Admin + warehouse can drive the lifecycle from the detail page.
  const canTransition =
    hasPermission(user, Permission.ORDERS_VIEW_ALL) &&
    order?.status &&
    order.status !== "draft" &&
    validNextOptions(order.status).some((s) => s !== "cancelled");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await ordersService.get(id);
      setOrder(data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmCancel = async (reason) => {
    setCancelSubmitting(true);
    try {
      const updated = await ordersService.cancel(order.id, reason);
      setOrder(updated);
      toast.success(t("orders.cancel_modal.success"));
      setCancelOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        status === 409
          ? t("orders.cancel_modal.conflict")
          : formatApiError(err, t("common.error_generic"))
      );
      if (status === 409) await load();
    } finally {
      setCancelSubmitting(false);
    }
  };

  const submitDamage = async (payload) => {
    setDamageSaving(true);
    try {
      // A13: force the order_id to this order — the modal locks it too,
      // but we belt-and-brace the server payload.
      await damagesService.create({ ...payload, order_id: order.id, product_id: payload.product_id });
      toast.success(t("admin.damages.created"));
      setDamageOpen(false);
      await load();
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "PRODUCT_NOT_IN_ORDER")
        toast.error(t("admin.damages.err_product_not_in_order"));
      else toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setDamageSaving(false);
    }
  };

  if (loading) return <FullPageSpinner />;
  if (notFound) {
    return (
      <div className="space-y-6" data-testid="order-detail-page">
        <BackLink onClick={() => navigate("/orders")} />
        <EmptyState
          title={t("orders.detail.not_found_title")}
          body={t("orders.detail.not_found_body")}
          actionLabel={t("orders.detail.back_to_list")}
          onAction={() => navigate("/orders")}
          testId="order-not-found"
        />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-6" data-testid="order-detail-page">
        <BackLink onClick={() => navigate("/orders")} />
        <ErrorState
          title={t("orders.error_title")}
          body={error}
          onRetry={load}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      <BackLink onClick={() => navigate("/orders")} />

      <header className="rounded-lg border border-border-subtle bg-surface p-6 shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span
                className="font-mono text-lg font-semibold text-text-primary"
                data-testid="order-detail-number"
              >
                {order.order_number ? `#${order.order_number}` : ""}
              </span>
              <StatusBadge status={order.status} size="lg" />
            </div>
            <h1
              className="mt-2 max-w-2xl text-2xl font-semibold leading-tight tracking-tight text-text-primary"
              data-testid="order-event-name"
            >
              {order.event_name}
            </h1>
            <div className="mt-1 text-sm text-text-secondary">
              {formatDateRange(order.from_date, order.until_date, i18n.language)}
            </div>
          </div>

          {order.cancellable && hasPermission(user, Permission.ORDERS_CANCEL_OWN) && (
            <Button
              onClick={() => setCancelOpen(true)}
              variant="outline"
              data-testid="order-detail-cancel"
              className="border-status-unavailable/30 text-status-unavailable hover:bg-status-unavailable/10"
            >
              <XCircle className="mr-2 h-4 w-4" />
              {t("orders.detail.cancel_button")}
            </Button>
          )}
          {canTransition && (
            <Button
              onClick={() => setTransitionOpen(true)}
              data-testid="order-detail-transition"
              className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
            >
              <ChevronRight className="mr-2 h-4 w-4" />
              {t("orders.detail.transition_button")}
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <InfoCard
            title={t("orders.detail.event_heading")}
            testId="event-card"
          >
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.event_name")}
                </dt>
                <dd className="mt-1 text-sm text-text-primary">
                  {order.event_name}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.bottles_count")}
                </dt>
                <dd className="mt-1 text-sm text-text-primary">
                  {order.bottles_count}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.period")}
                </dt>
                <dd className="mt-1 text-sm text-text-primary">
                  {formatDateRange(
                    order.from_date,
                    order.until_date,
                    i18n.language
                  )}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.remarks")}
                </dt>
                <dd
                  className="mt-1 whitespace-pre-line text-sm text-text-primary"
                  data-testid="order-remarks"
                >
                  {order.remarks?.trim()
                    ? order.remarks
                    : t("orders.detail.remarks_empty")}
                </dd>
              </div>
            </dl>
          </InfoCard>

          <InfoCard
            title={t("orders.detail.products_heading")}
            testId="products-card"
          >
            <ul
              className="divide-y divide-border-subtle"
              data-testid="order-items"
            >
              {order.items.map((it) => {
                const primaryImage =
                  (Array.isArray(it.image_urls_snapshot) && it.image_urls_snapshot[0]) ||
                  it.product_image_snapshot ||
                  null;
                return (
                <li
                  key={it.product_id + it.product_name_snapshot}
                  className="flex items-center gap-3 py-3"
                  data-testid={`order-item-${it.product_id}`}
                >
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-border-subtle bg-offwhite">
                    {primaryImage ? (
                      <img
                        src={primaryImage}
                        alt={it.product_name_snapshot}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-offwhite" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-text-primary">
                      {it.product_name_snapshot}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {t("orders.detail.product_col")}
                    </div>
                  </div>
                  <div className="pr-2 text-sm text-text-primary">
                    <span className="text-xs uppercase tracking-wide text-text-secondary">
                      {t("orders.detail.quantity_col")}
                    </span>
                    <span className="ml-2 font-semibold">
                      × {it.quantity}
                    </span>
                  </div>
                </li>
                );
              })}
            </ul>
          </InfoCard>

          <div className="grid gap-6 md:grid-cols-2">
            <InfoCard
              title={t("orders.detail.customer_heading")}
              testId="customer-card"
            >
              <AddressBlock address={order.customer} />
            </InfoCard>

            <InfoCard
              title={t("orders.detail.delivery_heading")}
              testId="delivery-card"
            >
              {/* A18.3 — never render a "Same as Billing" note on the
                  read side; always show the effective delivery block
                  as-is (falls back to the billing address when the
                  order was captured with `delivery_same_as_customer`). */}
              <AddressBlock address={order.delivery_address || order.customer} />
            </InfoCard>
          </div>

          {/* A18.3 — Damages block only surfaces on the return path.
              Bars are inspected during quality-testing, so hiding it
              before `collected` avoids polluting the earlier statuses
              (order_placed / confirmed / in_progress / shipped /
              delivered) and every draft with an empty inspection card. */}
          {["collected", "quality_testing", "collection_complete"].includes(order.status) && (
          <section
            className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card"
            data-testid="damages-card"
          >
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-text-primary">
                {t("orders.detail.damages_heading")}
              </h2>
              <div className="flex items-center gap-3">
                {canAddDamage && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setDamageOpen(true)}
                    data-testid="order-detail-add-damage"
                    className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    {t("orders.detail.damages_add")}
                  </Button>
                )}
                <div className="h-px w-6 bg-champagne" aria-hidden />
              </div>
            </div>
            {Array.isArray(order.damages) && order.damages.length > 0 ? (
              <ul
                className="divide-y divide-border-subtle"
                data-testid="order-damages"
              >
                {order.damages.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center gap-3 py-3"
                    data-testid={`order-damage-${d.id}`}
                  >
                    <AlertTriangle
                      className="h-4 w-4 shrink-0 text-taupe"
                      aria-hidden
                    />
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/damages?id=${encodeURIComponent(d.id)}`)
                      }
                      className="min-w-0 flex-1 text-left"
                      data-testid={`order-damage-link-${d.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-text-primary">
                          {d.damage_number}
                        </span>
                        <span className="truncate text-sm text-text-primary">
                          {d.product_name}
                        </span>
                      </div>
                    </button>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${SEVERITY_TONE[d.severity] || SEVERITY_TONE.minor}`}
                    >
                      {t(`admin.damages.severity_${d.severity}`)}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${DAMAGE_STATUS_TONE[d.status] || DAMAGE_STATUS_TONE.open}`}
                    >
                      {t(`admin.damages.status_${d.status}`)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                className="py-2 text-sm text-text-secondary"
                data-testid="order-damages-empty"
              >
                {t("orders.detail.damages_none")}
              </p>
            )}
          </section>
          )}
        </div>

        <div className="space-y-6">
          {order.status !== "draft" && <StatusTimeline order={order} />}

          <div
            className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card"
            data-testid="meta-card"
          >
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-text-primary">
                {t("orders.detail.meta_heading")}
              </h3>
              <div className="h-px w-6 bg-champagne" aria-hidden />
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.meta_created_at")}
                </dt>
                <dd className="text-right text-text-primary">
                  {formatDate(order.created_at, i18n.language)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-xs uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.meta_updated_at")}
                </dt>
                <dd className="text-right text-text-primary">
                  {formatDateTime(order.updated_at, i18n.language)}
                </dd>
              </div>
              <div className="pt-2 text-[11px] text-text-secondary">
                {t("orders.detail.meta_contact")}
              </div>
            </dl>
          </div>
        </div>
      </div>

      <CancelOrderModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={confirmCancel}
        submitting={cancelSubmitting}
        orderNumber={order.order_number}
      />

      <DamageFormModal
        open={damageOpen}
        onOpenChange={setDamageOpen}
        onSubmit={submitDamage}
        submitting={damageSaving}
        lockedOrderId={order.id}
        lockedOrderNumber={order.order_number}
      />

      <TransitionModal
        open={transitionOpen}
        onOpenChange={setTransitionOpen}
        order={order}
        onDone={() => load()}
      />
    </div>
  );
}

function BackLink({ onClick }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="order-detail-back"
      className="inline-flex items-center gap-1 text-xs text-text-secondary transition-colors hover:text-charcoal"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      {t("orders.detail.back_to_list")}
    </button>
  );
}
